import React from "react";
import { radToDeg, formatDMS } from "../lib/api";

/**
 * Circular compass showing Hz + a vertical arc showing V.
 * Pure SVG — no external deps, no animation of the numeric value.
 */
export const Compass = ({ state }) => {
  const hz = state?.hz ?? 0;
  const v = state?.v ?? Math.PI / 2;
  const hzDeg = radToDeg(hz);
  const vDeg = radToDeg(v);

  // Ticks every 15deg
  const ticks = Array.from({ length: 24 }, (_, i) => i * 15);

  return (
    <div className="panel" data-testid="compass-panel">
      <div className="panel-header">
        <span>AZIMUTH / ZENITH</span>
        <span className="text-[10px] text-[var(--text-mute)]">GEOCOM · 400 GON</span>
      </div>
      <div className="flex items-center gap-4 px-4 py-4">
        {/* Compass */}
        <div className="relative flex-1">
          <svg viewBox="-110 -110 220 220" className="w-full">
            {/* Outer ring */}
            <circle cx="0" cy="0" r="100" fill="none" stroke="#2a2f3a" strokeWidth="1.5" />
            <circle cx="0" cy="0" r="85" fill="none" stroke="#1a1d24" strokeWidth="1" />
            <circle cx="0" cy="0" r="55" fill="none" stroke="#1a1d24" strokeWidth="1" />

            {/* Ticks */}
            {ticks.map((deg) => {
              const rad = (deg * Math.PI) / 180;
              const inner = deg % 90 === 0 ? 78 : deg % 45 === 0 ? 85 : 92;
              const x1 = Math.sin(rad) * inner;
              const y1 = -Math.cos(rad) * inner;
              const x2 = Math.sin(rad) * 100;
              const y2 = -Math.cos(rad) * 100;
              return (
                <line
                  key={deg}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={deg % 90 === 0 ? "#00ff41" : "#3b4252"}
                  strokeWidth={deg % 90 === 0 ? 2 : 1}
                />
              );
            })}
            {/* Cardinal letters */}
            <text x="0" y="-68" fontSize="10" fill="#00ff41" textAnchor="middle" fontFamily="JetBrains Mono">N</text>
            <text x="68" y="4" fontSize="10" fill="#3b4252" textAnchor="middle" fontFamily="JetBrains Mono">E</text>
            <text x="0" y="74" fontSize="10" fill="#3b4252" textAnchor="middle" fontFamily="JetBrains Mono">S</text>
            <text x="-68" y="4" fontSize="10" fill="#3b4252" textAnchor="middle" fontFamily="JetBrains Mono">W</text>

            {/* Crosshair */}
            <line x1="-6" y1="0" x2="6" y2="0" stroke="#3b4252" />
            <line x1="0" y1="-6" x2="0" y2="6" stroke="#3b4252" />

            {/* Hz needle */}
            <g transform={`rotate(${hzDeg})`}>
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="-96"
                stroke="#00e5ff"
                strokeWidth="2"
                strokeLinecap="round"
                filter="url(#glow)"
              />
              <polygon points="-5,-90 5,-90 0,-102" fill="#00e5ff" />
            </g>

            <defs>
              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="1.2" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
          </svg>
          <div className="absolute inset-x-0 bottom-1 text-center">
            <div className="text-[10px] tracking-[0.3em] text-[var(--text-dim)]">Hz</div>
            <div className="readout text-sm font-semibold text-[var(--cyan)] glow-cyan">
              {formatDMS(hz)}
            </div>
          </div>
        </div>

        {/* Vertical arc */}
        <div className="relative flex flex-col items-center">
          <svg viewBox="-40 -110 80 220" className="h-48 w-16">
            <path d="M 0,-100 A 100,100 0 0,1 0,100" stroke="#2a2f3a" strokeWidth="1.5" fill="none" />
            <path
              d="M 0,-100 A 100,100 0 0,1 0,100"
              stroke="#00ff41"
              strokeWidth="2"
              fill="none"
              strokeDasharray="314"
              strokeDashoffset={314 - (314 * vDeg) / 180}
              opacity="0.5"
            />
            {/* Marker */}
            {(() => {
              const rad = ((vDeg - 90) * Math.PI) / 180;
              const x = Math.cos(rad) * 100;
              const y = Math.sin(rad) * 100;
              return <circle cx={x} cy={y} r="6" fill="#00ff41" filter="url(#glow2)" />;
            })()}
            {/* Horizon marker */}
            <line x1="-8" y1="0" x2="8" y2="0" stroke="#ffb000" strokeWidth="1.5" strokeDasharray="2 2" />
            <defs>
              <filter id="glow2" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
          </svg>
          <div className="mt-1 text-[10px] tracking-[0.3em] text-[var(--text-dim)]">V</div>
          <div className="readout text-sm font-semibold text-[var(--neon)] glow-green">
            {formatDMS(v)}
          </div>
        </div>
      </div>
    </div>
  );
};
